/**
 * CloudMarch — ONE heap cloud's shape and march, for whoever draws it (2026-10-07, the cloud-realism work).
 *
 * The volumetric field (CloudMeshField: a pooled mesh per cloud, its parameters in uniforms) and the far layer
 * (CloudImpostorLayer: one instanced draw, its parameters in per-instance attributes) used to be two different
 * pictures of a cloud: the field marched the turret union; the far layer painted the same turrets as shaded discs on a
 * card — bunches of grey balls on the horizon under a sky of lit heaps (the first far pictures). This module is the
 * march both now run, written against a SOURCE of nodes (a cloud's base point, semi-axes, turrets, seed, character) so
 * a uniform-backed cloud and an attribute-backed one share every line: the same silhouette, the same florets, the same
 * light (CloudLighting) — the far layer only takes fewer, cheaper samples.
 *
 * Everything here is PLAIN functions over TSL nodes (never `Fn`-wrapped: nested Fn parameter substitution silently
 * drops arguments in WGSL — the CloudRenderer gotcha); `march` uses toVar / Loop / If and must be called inside the
 * caller's own `Fn`.
 */
import {
  If, Loop, vec2, vec3, float, uint,
  exp, max, min, abs, step, clamp, dot, mix, fract, sin,
  pow, normalize, length, distance, smoothstep, texture3D, texture,
  screenCoordinate, sqrt, sign,
} from 'three/tsl';
import type * as THREE from 'three/webgpu';
import { getBlueNoiseTexture } from '../bluenoise/BlueNoiseRuntime.js';
import { MAX_LOBES, LOBE_SMIN_K, LOBE_FLAT } from './CloudLobes.js';
import { cloudPhase, cloudMultiScatter, CLOUD_CREVICE_AO } from './CloudLighting.js';

type N = any;

/**
 * THE OPAQUE CUT (FIX 2026-10-07, the first in-game picture of the wave). The march stops once 2 % of the light behind
 * the cloud is left, and `alpha = 1 − transmittance` then left a "solid" cloud 2 % see-through. Against the sky that is
 * nothing — but the sun's disc is drawn ~1000× the sky, and 2 % of it is still white: the disc showed as a hard white
 * dot through the middle of a thick cumulus' dark base (invisible in the beds, whose sky carried no disc; unseen before
 * the wave, when every cloud was a pale see-through shape anyway). A real cumulus (τ 30–100) passes ~10⁻¹³. The cut IS
 * opaque: alpha and the in-scattered light are normalised by what the march integrated (1 − cut), so a ray that reached
 * the cut hides what is behind it completely; a thin edge keeps its transmittance (+2 %) and still shows the disc.
 */
export const CLOUD_OPAQUE_CUT = 0.02;

/**
 * THE BODY IS SOLID (2026-10-07, the cloud-realism work): how much of the in-volume noise survives the subtractive gate
 * inside a turret (the gate is 1 − this). It was the field's GLOBAL coverage × 1.35 — a fair sky (cover 0.3) therefore
 * carved every cumulus down to its densest noise peaks: a scatter of shreds and specks ("popcorn") where a real
 * fair-weather cumulus is one solid white heap. Coverage decides HOW MANY clouds stand in the sky and how big they are
 * (the weather map's threshold, the cell streamer); what a cloud IS does not thin with the weather.
 */
export const CLOUD_BODY_SOLIDITY = 0.8;
/** March: sphere-trace iterations to the turret skin, and the step's growth per sample from the skin inward. */
const SKIN_TRACE_STEPS = 6;
/** Bisections back to the skin when a coarse step first finds cloud (THE LIT SKIN). */
const SKIN_BISECT = 3;
const STEP_GROWTH = 1.2;

/** One cloud's parameters as TSL nodes (uniforms for a pooled mesh, per-instance attributes for the far layer). */
export interface CloudMarchSource {
  /** The cloud's BASE point (the dome's equator centre), ABSOLUTE world metres (vec3). */
  center: N;
  /** Horizontal semi-axis (m) and the DOME HEIGHT (m). */
  radius: N;
  height: N;
  /** x = density mul, y = fringe-erosion mul, z = wind-aligned noise stretch (vec3). */
  params: N;
  /** Noise seed offset, metres (vec3). */
  seed: N;
  /** Turret `i` (0 … MAX_LOBES − 1) in shape space: (x, y, z, r) (vec4). */
  lobe(i: number): N;
  /** The ground's light under this cloud (the provider's units; × `uplightScale`) (vec3). */
  uplight: N;
  /** CONDENSATION: how far the skin is still retreated, 0.5 (just born) → 0 (grown) (float). */
  condense: N;
  /** March-step multiplier (1 near → larger far) (float). */
  lod: N;
}

/** What every cloud of a field shares. */
export interface CloudMarchEnv {
  /** The field's shared uniform nodes (CloudMeshField.uniforms). */
  uniforms: {
    sunDirection: N; sunColor: N; sunIlluminance: N; ambientBottom: N; ambientTop: N; densityScale: N;
    ambientTopColor: N; ambientBottomColor: N; albedo: N; multiScatter: N;
    uFlashPos: N; uFlashIntensity: N; uFlashRadius: N; uplightScale: N; uplightFalloff: N;
  };
  baseNoise: THREE.Data3DTexture;
  detailNoise: THREE.Data3DTexture;
  wind: N; evolve: N; worldOffset: N; noiseScale: N; extinction: N; windDir: N;
  /**
   * THE CLOUDS' AIR (see `cloudAir`): two exponential layers of clear air — each a sea-level extinction per metre per
   * channel and a scale height (m) — and the haze the scene's fog adds.
   */
  airLow: N; airLowScale: N; airHigh: N; airHighScale: N; hazeColor: N; hazeDensity: N;
}

/**
 * THE CLOUDS' AIR (2026-10-07, the cloud-realism work). A cloud is seen THROUGH the air: its light is dimmed and the
 * air's own light is added, more in blue than in red — at a few kilometres a cumulus's shaded side is blue-grey and its
 * sunlit side barely changed; at forty it has sunk, cream and low in contrast, into the horizon. The clouds had none of
 * it: the far layer faded by the scene's FogExp2 alone (one grey law — and a consumer whose clear air is drawn by aerial
 * froxels keeps that fog at ZERO on a clear day, so its far clouds stood crisp and neutral in front of a hazy horizon),
 * the volumes by nothing.
 *
 *   τ   = Σ layers  β₀ · e^(−h₀/H) · d · (1 − e^(−a)) / a,   a = Δh / H     the layer's extinction integrated along the
 *                                           sight line from the eye (height h₀) to the cloud (Δh higher, d away): the
 *                                           air thins with height, so a storm's anvil 6 km up keeps its edge at a
 *                                           distance where the cumulus bases under it have gone into the haze
 *   T   = e^(−τ) · e^(−(ρ·d)²)            ρ: the scene fog's density (its weather, FogExp2's law)
 *   out = cloud · T + airLight · α · (1 − T)   airLight: `hazeColor` — the horizon's radiance, which IS the air's light
 *                                           over an endless level path (premultiplied: the air in front of a cloud
 *                                           pixel replaces the sky's only by that pixel's coverage)
 *
 * The layers are the caller's atmosphere (Atlas: the boundary-layer aerosol, 1.2 km; Rayleigh + the background aerosol,
 * 8 km). β₀ = 0 (the default) leaves the fog's term alone — the far layer exactly as it was.
 */
export function cloudAir(env: CloudMarchEnv, scat: N, alpha: N, dist: N, eyeY: N, rdY: N): N {
  const h0 = max(eyeY, float(0));
  const dh = rdY.mul(dist);
  const layer = (beta: N, scale: N): N => {
    // (1 − e^(−a)) / a, guarded round a = 0 and for a sight line that DROPS (a < 0: denser air — bounded at two scale heights)
    const a = clamp(dh.div(scale), float(-2), float(40));
    const aa = max(abs(a), float(1e-3)).mul(sign(a).add(step(abs(a), float(0)).mul(1)));
    const mean = float(1).sub(exp(aa.negate())).div(aa);
    return vec3(beta as never).mul(exp(h0.div(scale).negate())).mul(dist).mul(mean);
  };
  const tau = layer(env.airLow, env.airLowScale).add(layer(env.airHigh, env.airHighScale));
  const fogT = exp(dist.mul(env.hazeDensity).pow(2).negate());
  const T = exp(tau.negate()).mul(fogT);
  return scat.mul(T).add(vec3(env.hazeColor as never).mul(alpha).mul(vec3(1).sub(T)));
}

export interface CloudMarchOptions {
  /** Primary march samples per fragment. */
  steps: number;
  /** Sun-cone samples per lit sample: a uniform node (a tier raises it live) or a constant. */
  sunSteps: N | number;
  /** One base fetch per density sample (no second floret octave, no detail erosion) — the far layer's form. */
  cheap?: boolean;
}

export interface CloudMarch {
  /** The cloud's base point in RENDER space, its semi-axes, its dome height. */
  centerR(): N; r3(): N; hgt(): N;
  lobeUnion(rel: N): { sdf: N; rNear: N; cNear: N };
  sampleShape(p: N, cheap: boolean): { d: N; floret: N; up: N; hLocal: N; skip: N };
  /** The optical depth from a point toward the sun. */
  sunCone(p: N): N;
  /**
   * March the cloud along a render-space ray (inside the caller's `Fn`): the premultiplied in-scattered radiance and
   * the opacity, both as vars, plus the ray–ellipsoid terms (for a debug view). `gate` (optional bool node) skips the
   * march — a fully faded cloud costs the intersection alone.
   */
  march(ro: N, rd: N, gate?: N): { scat: N; alpha: N; disc: N; b: N; a2: N };
}

export function buildCloudMarch(u: CloudMarchSource, env: CloudMarchEnv, opts: CloudMarchOptions): CloudMarch {
  const S = env.uniforms;
  const baseNoiseTex = env.baseNoise;
  const detailNoiseTex = env.detailNoise;
  const uWind = env.wind, uEvolve = env.evolve, uWorldOffset = env.worldOffset;
  const uNoiseScale = env.noiseScale, uExtinction = env.extinction, uWindDir = env.windDir;
  const uSunSteps = opts.sunSteps;
  const cheap = !!opts.cheap;

  // TSL helpers as PLAIN functions (NOT Fn-wrapped) — the CloudRenderer
  // gotcha: nested Fn parameter substitution silently drops args in WGSL.
  // Render-space sphere center: u.center is ABSOLUTE world (stable across
  // FP64 rebase snaps); the ray/positions here are RENDER space, so add
  // worldOffset (= scene.position). Without this the analytic sphere sits
  // at the wrong place whenever the origin has rebased (>10 km from spawn)
  // — invisible at spawn where scene.position ≈ 0, catastrophic later.
  // u.center is the cloud's BASE point (the hemisphere's equator) — absolute.
  const centerR = (): any => vec3(u.center as never).add(uWorldOffset);
  // Semi-axes (m): rx = rz = radius, y = the DOME HEIGHT (the source's `height` —
  // humilis wider than tall). All shape math runs in "shape space"
  // (positions ÷ r3): the unit ball with the flat base through y = 0 and the
  // top at y = 1; only the upper half is cloud.
  const hgt = (): any => u.height as any;
  const r3 = (): any => vec3(u.radius as never, hgt(), u.radius as never);
  // Turret union (shape space): polynomial smooth-min over the lobes (the
  // CPU mirror is CloudLobes.lobeUnionSdf), plus the radius of the NEAREST
  // lobe — its scale sets the erosion band. Unrolled, branch-free.
  const lobeUnion = (rel: any): { sdf: any; rNear: any; cNear: any; flatNear: any } => {
    let dS: any = float(10);
    let dRaw: any = float(10);
    let rNear: any = float(0.5);
    let cNear: any = vec3(0, 0.3, 0); // the nearest lobe's centre — the skin's facing (the ambient split)
    let flatNear: any = float(0);
    for (let i = 0; i < MAX_LOBES; i++) {
      const c = u.lobe(i);
      // A FLAT lobe (an anvil) carries a NEGATIVE radius: |w| wide, LOBE_FLAT·|w| thick — the distance is taken in the
      // lobe's own squashed space and scaled back by the thin axis (never over-estimated: the skin trace stays safe).
      // A round lobe's `sq` is 1: the arithmetic of before.
      const ra = abs(c.w);
      const live = step(float(0.001), ra);
      const flat = step(c.w, float(-0.001));
      const sq = mix(float(1), float(LOBE_FLAT), flat);
      const dvec = rel.sub(c.xyz).div(vec3(float(1), sq, float(1)));
      const di = mix(float(10), length(dvec).sub(ra).mul(sq), live);
      const h = max(float(LOBE_SMIN_K).sub(abs(dS.sub(di))), float(0)).div(LOBE_SMIN_K);
      dS = min(dS, di).sub(h.mul(h).mul(LOBE_SMIN_K * 0.25));
      const nearer = step(di, dRaw);
      rNear = mix(rNear, ra.mul(sq), nearer);
      cNear = mix(cNear, c.xyz, nearer);
      flatNear = mix(flatNear, flat, nearer);
      dRaw = min(dRaw, di);
    }
    return { sdf: dS, rNear, cNear, flatNear };
  };

  /**
   * The cloud at a render-space point: its density, and what the lighting reads from the same samples — the florets'
   * crest / trough, how far the skin there faces UP, the height in the dome. `cheap` is the sun cone's form: ONE base
   * fetch (the first floret octave carves the skin; no second octave, no detail erosion — a shadow resolves neither).
   */
  const sampleShape = (p: any, cheap: boolean): { d: any; floret: any; up: any; hLocal: any; skip: any } => {
    // Absolute world position (undo the origin rebase) for the noise UVs.
    const pAbs = p.sub(uWorldOffset);

    // Volume shape: the NOISE defines the puff; the sphere is only a bound.
    // The first cut multiplied the noise by a smooth radial envelope
    // (env = 1−r²), which made the ENVELOPE the shape: along tangential
    // rays the outer shell accumulates enough optical depth to draw the
    // bounding sphere as a hard circle (the "clouds with spheres around
    // them" report, worst at dusk/night against a dark sky). Instead a
    // SUBTRACTIVE boundary guard rises steeply over the outer shell, so
    // density is guaranteed 0 well inside the bound and the silhouette is
    // carved out of the noise — lumpy, never a circle.
    // The guard must kill the max possible noise (puffed ≤ 1.35) WITHOUT
    // help from the coverage term: at storm coverage the (1−cov) gate
    // collapsed and the old 1.45-amplitude guard only won in the outermost
    // shell — the 0.55–0.9·r band filled in and the volume drew as a soft
    // BALL (the night "orb around each cloud" report). 1.6 by 0.85·r beats
    // every possible noise value standalone (margin 0.25) while matching
    // the old interior extent at normal coverage (gate@0.6·r ≈ 0.61 vs the
    // old 0.82 — slightly fuller mid-shell, still noise-carved).
    const rel = p.sub(centerR()).div(r3()); // shape space (unit ball, base at y = 0)
    // THE TURRET GUARD (2026-09-10, "all pill-shaped"): the subtractive
    // boundary guard used to follow the radial distance of ONE ellipsoid, so
    // every silhouette was a convex lozenge whatever the noise did. It now
    // follows the smooth union of the cloud's turret lobes — rising over the
    // outer ~45 % of the nearest lobe's radius to 1.6 (≥ max noise + margin),
    // so density is 0 outside the union and the outline is carved from the
    // noise inside a LUMPY bound: several rounded tops of different heights
    // on one flat base, never a pill.
    const { sdf, rNear, cNear, flatNear } = lobeUnion(rel);
    const sdfNorm = sdf.div(max(rNear, float(0.12)));
    const hLocal = clamp(rel.y, float(0), float(1));
    // FLAT BASE at the condensation level: the plane y = 0 of shape space
    // (the hemisphere's equator — a wide flat underside, the strongest
    // cumulus cue); a sharp ramp over the bottom 5 % of the dome (2026-10-07:
    // at 12 % a 600 m dome faded its base over 70 m — a soft underside, not
    // the level floor every photograph shows). The tops are the turrets'; a
    // last soft cap keeps the noise inside the ball.
    const vertWin = smoothstep(float(0.0), float(0.05), rel.y)
      .mul(float(1).sub(smoothstep(float(0.86), float(1.0), rel.y)));

    // Wind-aligned noise anisotropy (u.params.z): the along-wind coordinate
    // is divided by (1 + stretch), elongating features into streaks — real
    // cirrus is "hair-like", combed along the upper wind. stretch 0 is the
    // identity, so cumulus/stratocumulus pay only the rotation arithmetic.
    const stretchDiv = float(1).add(vec3(u.params as never).z);
    const along = pAbs.x.mul(uWindDir.x).add(pAbs.z.mul(uWindDir.y)).div(stretchDiv);
    const cross = pAbs.z.mul(uWindDir.x).sub(pAbs.x.mul(uWindDir.y));
    const pAbsS = vec3(
      along.mul(uWindDir.x).sub(cross.mul(uWindDir.y)),
      pAbs.y,
      along.mul(uWindDir.y).add(cross.mul(uWindDir.x)),
    );

    // CLOUD-RELATIVE FREQUENCY: the base period is ~0.6 of the cloud's
    // radius (263 m at r 450 — about three florets across a lobe) — a fixed world period left a 120 m turret
    // seeing half a noise period, i.e. a smoothly inflated ball. Still
    // world-anchored (wind scroll, evolution, the per-cloud seed).
    // A BIG CLOUD HAS MORE FLORETS, NOT BIGGER ONES (2026-10-07): the period follows the radius only up to 300 m; past it
    // it grows as radius^0.6. With period ∝ radius a 700 m tower wore three 400 m pock-marks across its face (the first
    // congestus pictures: swiss cheese); real cauliflower is fractal — a tower's florets are barely larger than a small
    // cumulus's. `big` = (300 / radius)^0.4 ≤ 1; the carve's depth goes by its root (deep enough to read as cauliflower
    // on a tower's flank — by `big` itself a near congestus was a smooth boulder).
    const sizeRatio = float(300).div(max(float(u.radius as never), float(80)));
    const big = pow(min(sizeRatio, float(1)), float(0.4));
    const freq = uNoiseScale.mul(1.5).mul(sizeRatio).div(big);
    // Base noise: world-anchored + per-cloud seed + wind scroll + the
    // POSITIVE half of the net-zero evolution drift.
    const uvwBase = pAbsS.add(u.seed).add(uWind).add(uEvolve).mul(freq);
    const baseSample = texture3D(baseNoiseTex, uvwBase);
    const midFBM = baseSample.g.mul(0.625)
      .add(baseSample.b.mul(0.25))
      .add(baseSample.a.mul(0.125));
    const dilated = clamp(
      baseSample.r.sub(midFBM.sub(1).mul(0.5)), float(0), float(1));

    // THE CAULIFLOWER CARVE (2026-09-10, the "balloon cluster" pictures):
    // the lobe union's SDF is pushed OUTWARD by a contrast-stretched
    // two-octave fBm, so the surface retreats from the lobe skin by up to
    // 0.55 of the lobe radius where the noise is low and stays at the skin
    // where it peaks — florets within florets, the inverted-Worley billow
    // of the Nubis line. A gate on the plain SDF with the noise merely
    // MODULATING density moved the surface a tenth of a radius and every
    // turret drew as a smooth sphere. The carve only ever retreats inward,
    // so the density stays inside the mesh bound (the M5 doctrine holds).
    // Florets come from the two LOW octaves only (features ≈ a lobe and
    // half a lobe); with the 4× detail in the carve the contrast stretch
    // amplified it into grainy speckle (the second pictures) — the detail
    // texture only erodes the edges below.
    // Second octave (2.3×) and the detail texture (4×, the NEGATIVE
    // evolution half — net-zero): florets at ~0.4, ~0.2 and ~0.1 of a lobe.
    // The far form's second octave rides in the SAME fetch (G = the bake's mid-frequency fBm): one octave alone drew a
    // far tower's florets as a few broad pits.
    let fbm: any = baseSample.r.mul(0.62).add(baseSample.g.mul(0.38));
    let erodeAmt: any = null;
    if (!cheap) {
      const base2 = texture3D(baseNoiseTex, uvwBase.mul(2.3).add(vec3(0.37, 0.11, 0.73)));
      fbm = baseSample.r.mul(0.62).add(base2.r.mul(0.38));
      const uvwDetail = pAbsS.add(u.seed).add(uWind).sub(uEvolve).mul(freq).mul(4);
      const detailSample = texture3D(detailNoiseTex, uvwDetail);
      const detailFBM = detailSample.r.mul(0.625)
        .add(detailSample.g.mul(0.25))
        .add(detailSample.b.mul(0.125));
      // Detail erosion (wispy at the base, billowy at the top).
      erodeAmt = mix(detailFBM, float(1).sub(detailFBM), clamp(hLocal.mul(2), float(0), float(1)));
    }
    const floret = smoothstep(float(0.33), float(0.67), fbm); // contrast: distinct florets
    // CONDENSATION: a cloud born in view starts with its surface retreated a
    // further 0.5 radius (small cores on the base) and grows out over
    // u.grow seconds until the florets reach the lobe skins.
    const condense = u.condense;
    // 0.55 r deep: at 0.75 the troughs cut through to the lobe cores and a
    // cloud fell apart into drifting pieces (the third pictures).
    // THE BASE IS LEVEL AND WHOLE (2026-10-07, against the photographs): florets are convection's — they grow UP the
    // cloud. At the condensation level a cumulus is one flat grey sheet with a smooth outline; carved as deep there as on
    // the crown, every cloud seen from the ground (we look at bases!) was a cluster of popcorn with notches and holes in
    // its floor, and its necks were cut into drifting crumbs. The carve is 0.3 of itself at the base and whole from a
    // third of the way up; 0.5 r deep at most (was 0.55).
    const carveH = mix(float(0.3), float(1), smoothstep(float(0.03), float(0.34), rel.y));
    // (an anvil is ice: smooth and fibrous — a third of the carve)
    // (a GIANT's florets are shallow — min(1, 2.2·big) is 1 up to a 2 km radius: at 5 km a pit a third of a turret deep
    // was 350 m of shadow, and a far storm drew as white dots on blue)
    const carveDepth = sqrt(big).mul(0.5).mul(min(big.mul(2.2), float(1)));
    const sdfC = sdfNorm.add(float(1).sub(floret).mul(carveDepth).mul(carveH).mul(mix(float(1), float(0.35), flatNear))).add(condense);
    // THE CRISP SKIN (2026-10-07): the guard rises over 0.19 of a lobe radius
    // (was 0.45). A growing cumulus turret has a hard, cauliflower outline;
    // the wide ramp gave every cloud a 60–100 m band of thin haze round it —
    // cotton wool. The march now steps finely AT the skin (see the march), so
    // a steep gate no longer bands.
    const boundGate = smoothstep(float(-0.16), float(0.03), sdfC).mul(1.6);

    // A BIGGER CLOUD IS MORE SOLID (2026-10-07): the body's own noise may thin a small cumulus, but kilometres of cloud
    // have no see-through pockets — a far tower's few samples found them and drew a trunk full of holes. `big` is 1 up to
    // a 300 m radius (the noise whole) and falls with size: a congestus keeps a third of it, a cumulonimbus none to speak of.
    const puffed = mix(float(1), dilated, big).mul(vertWin.mul(1.4));

    // The body's gate + the boundary guard, both SUBTRACTIVE (Schneider remap
    // form). The gate is CLOUD_BODY_SOLIDITY's — never the global coverage
    // (see the constant): the interior stays noise-carved (never a solid
    // ball), the sphere-edge kill is boundGate's job alone.
    const gate = float(1 - CLOUD_BODY_SOLIDITY).add(boundGate);
    const preErode = clamp(puffed.sub(gate), float(0), float(1));
    let dens: any = preErode;
    if (erodeAmt) {
      // Edge-wispy erosion (Nubis doctrine: wispy at the faint edges, billowy
      // in the cores): erode hard where density is LOW, gently where high —
      // fringes shred into wisps while cores stay solid cauliflower.
      const erodeScale = mix(float(0.45), float(0.15),
        clamp(preErode.mul(3), float(0), float(1)))
        .mul(vec3(u.params as never).y); // genus erosion (cirrus shreds hard)
      dens = clamp(preErode.sub(erodeAmt.mul(erodeScale)), float(0), float(1));
    }
    // Genus density (stratocumulus > 1 ⇒ self-shadowed gray deck;
    // cirrus ≪ 1 ⇒ translucent veil).
    const d = dens.mul(S.densityScale).mul(vec3(u.params as never).x);

    // THE SKIN'S FACING (2026-10-07): how far the surface here looks UP — the
    // nearest turret's sphere normal taken back to world axes (÷ the semi-axes),
    // turning straight DOWN over the flat base. The ambient reads it: sky light
    // on what faces the sky, the ground's light on the underside (CloudLighting).
    const nS = rel.sub(cNear).div(r3());
    const baseFacing = float(1).sub(smoothstep(float(0.02), float(0.16), rel.y));
    const up = mix(nS.y.div(max(length(nS), float(1e-6))), float(-1), baseFacing);
    // How far (shape space) the carved skin still is from here — the march's empty-space skip.
    const skip = max(sdfC.sub(0.03), float(0)).mul(max(rNear, float(0.12)));
    return { d, floret, up, hLocal, skip };
  };

  /**
   * The optical depth from `p` toward the sun. Samples are spaced by a POWER law (segment ends at reach·((i+1)/N)^2.2)
   * so the first one sits a few per cent of the dome height away and the last reaches out of the cloud: a floret
   * shadows its neighbour 30 m away AND the far side of a 600 m dome goes dark. The old equal steps of half the dome
   * height saw only the bulk — no small-scale shading on the lit side (2026-10-07). The count is a UNIFORM (a tier
   * raises it live); the reach is the same whatever the count.
   */
  const sunCone = (p: any): any => {
    const L = normalize(S.sunDirection);
    const od = float(0).toVar('od');
    // The reach is a TURRET's, not the tower's: 0.9 of the dome height, at most 1.3 radii. A 2.4 km congestus spread its
    // few samples over 2.2 km (the first at 280 m) and its self-shadow came out as hard blotches the shape of whatever
    // stood at exactly that distance; what shades a floret is the turret beside it.
    // …and never past 1.5 km: at 25 m a mean free path the sun is long gone by then, and a giant's samples belong near.
    const reach = min(min(hgt().mul(0.9), float(u.radius as never).mul(1.3)), float(1500)).max(float(60));
    const tPrev = float(0).toVar('conePrev');
    Loop({ start: uint(0), end: uint(uSunSteps as never), type: 'uint' }, ({ i }) => {
      const tEnd = reach.mul(pow(float(i).add(1).div(float(uSunSteps as never)), float(2.2)));
      const seg = tEnd.sub(tPrev);
      const sp = p.add(L.mul(tPrev.add(seg.mul(0.6))));
      od.addAssign(sampleShape(sp, true).d.mul(uExtinction).mul(seg));
      tPrev.assign(tEnd);
    });
    return od;
  };

  const march = (ro: N, rd: N, gate?: N): { scat: N; alpha: N; disc: N; b: N; a2: N } => {
    // Analytic ray-ELLIPSOID (render space; centerR() rebase-corrects the
    // absolute cell center). Shape space: divide by the semi-axes — the
    // ellipsoid becomes a unit sphere; t stays in world metres because rd
    // is unit-length in world space.
    const oc = ro.sub(centerR()).div(r3());
    const rdS = rd.div(r3());
    const a2 = dot(rdS, rdS);
    const b = dot(oc, rdS);
    const cc = dot(oc, oc).sub(float(1));
    const disc = b.mul(b).sub(a2.mul(cc));

    const scat = vec3(0).toVar('scat');
    const alpha = float(0).toVar('alpha');

    If(disc.greaterThan(float(0)), () => {
      const sq = disc.sqrt();
      const t0 = b.negate().sub(sq).div(a2);
      const t1Full = b.negate().add(sq).div(a2);
      // Camera inside the volume → start at 0; then CLIP the chord to the
      // dome: the mesh is the ball's upper half, so the base plane cuts the
      // chord where the ray crosses y = base (a rising ray enters there, a
      // falling ray leaves there).
      const tStart = max(t0, float(0)).toVar('tStart');
      const t1 = t1Full.toVar('tEnd');
      const tPlane = centerR().y.sub(ro.y).div(rd.y);
      If(rd.y.greaterThan(float(1e-5)), () => { tStart.assign(max(tStart, tPlane)); })
        .ElseIf(rd.y.lessThan(float(-1e-5)), () => { t1.assign(min(t1, tPlane)); });
      const marchGate = gate ? t1.greaterThan(tStart).and(gate) : t1.greaterThan(tStart);

      If(marchGate, () => {
        // THE SKIN FIRST (2026-10-07): sphere-trace the turret union — seven sphere
        // distances a step, no texture fetch — to where the cloud can begin, then
        // march from there with steps that start FINE (1.2 % of the dome height,
        // ≥ 4 m) and grow 20 % a sample. A cloud is opaque within ~100 m of its
        // skin, so the samples belong there: the old march spread 20 equal steps
        // over the whole chord (30–50 m each) and found the surface to within a
        // step — every outline was a band of blue-noise grain, every floret a
        // blur. The same sample count now draws a crisp edge and still reaches
        // through a kilometre of thin cloud (4 m · (1.2²⁰ − 1)/0.2 ≈ 750 m).
        const shapePerMetre = max(length(rdS), float(1e-6)); // shape-space units per world metre along the ray
        const tCur = tStart.toVar('tCur');
        Loop({ start: uint(0), end: uint(SKIN_TRACE_STEPS), type: 'uint' }, () => {
          const relT = ro.add(rd.mul(tCur)).sub(centerR()).div(r3());
          tCur.addAssign(max(lobeUnion(relT).sdf, float(0)).div(shapePerMetre).mul(0.92));
        });
        // Distance LOD: u.lod stretches the first step (1 near → lodFarStep at
        // the field's edge); the loop's t-guard skips the samples past the exit.
        const stepCur = max(hgt().mul(0.012), float(4)).mul(u.lod).toVar('stepCur');

        // Blue-noise jitter (global LUT at 1:1 pixel:texel) of the PHASE of the
        // geometric sequence, not of its start: sample n sits at s₀·(gⁿ⁺ʲ − 1)/(g − 1),
        // so a pixel's samples slide through the whole gap to the next one at EVERY
        // depth. A start offset of one first step (a few metres) left the deep
        // samples — 20–30 m apart in a trough — in the same shells for every
        // pixel: contour lines across each shaded hollow (the first pictures).
        const bnTex = getBlueNoiseTexture();
        const jitter = bnTex
          ? texture(bnTex, screenCoordinate.xy.div(float(bnTex.image.width))).r
          : fract(sin(dot(screenCoordinate.xy, vec2(12.9898, 78.233))).mul(43758.5453));
        const gj = pow(float(STEP_GROWTH), jitter);
        tCur.addAssign(stepCur.mul(gj.sub(1)).div(STEP_GROWTH - 1));
        stepCur.mulAssign(gj);
        const trans = float(1).toVar('trans');
        // THE LIT SKIN (2026-10-07): what the eye sees of a cloud is its first mean free path — there the sun is barely
        // dimmed. A march whose step is longer than that (a far card's, a giant's: 50–200 m) lands its first sample
        // deep inside, where the sun is already half gone, and paints the whole slab with that light: far towers came
        // out a third as bright as near ones, a far cumulonimbus a blue ghost. So the first time a COARSE step finds
        // cloud, the ray is bisected back to the skin (three cheap samples) and restarts there at one mean free path.
        // A fine march (the near volumes) never takes the branch.
        const tEmpty = tCur.toVar('tEmpty');
        const entered = float(0).toVar('entered');
        const skinStep = clamp(float(1.2).div(max(uExtinction, float(1e-4))), float(6), float(80));

        // The light (CloudLighting): single scattering × the phase toward the
        // eye + the isotropic multiple scattering, both behind the optical depth
        // toward the sun; the ambient by the skin's facing.
        const cosTheta = dot(rd, normalize(S.sunDirection));
        const phase = cloudPhase(cosTheta);

        Loop({ start: uint(0), end: uint(opts.steps), type: 'uint' }, () => {
          If(tCur.lessThan(t1).and(trans.greaterThan(float(CLOUD_OPAQUE_CUT))), () => {
            const p = ro.add(rd.mul(tCur));
            const sh = sampleShape(p, cheap);
            const dense = sh.d.greaterThan(float(0.003));
            If(dense.and(entered.lessThan(float(0.5))).and(stepCur.greaterThan(skinStep)), () => {
              const lo = tEmpty.toVar('skinLo'), hi = tCur.toVar('skinHi');
              for (let k = 0; k < SKIN_BISECT; k++) {
                const mid = lo.add(hi).mul(0.5);
                const inside = step(float(0.003), sampleShape(ro.add(rd.mul(mid)), true).d);
                hi.assign(mix(hi, mid, inside));
                lo.assign(mix(mid, lo, inside));
              }
              tCur.assign(hi);
              stepCur.assign(skinStep);
              entered.assign(1);
            }).ElseIf(dense, () => {
              entered.assign(1);
              const stepOD = sh.d.mul(uExtinction).mul(stepCur);
              const stepT = exp(stepOD.negate());

              const sunOD = sunCone(p);
              const sunContrib = S.sunColor.mul(S.sunIlluminance)
                .mul(exp(sunOD.negate()).mul(phase).add(cloudMultiScatter(sunOD).mul(S.multiScatter)));

              const hLocal = sh.hLocal; // 0 base → 1 top
              // THE UPLIGHT (the night-sky realism wave, 2026-10-06): the ground's light under this cloud (a lit
              // town's lamps) on the BASE, decaying up the dome — the orange underside of a city's overcast.
              // uplightScale 0 (the default) adds exactly nothing.
              const uplight = vec3(u.uplight as never).mul(S.uplightScale)
                .mul(exp(S.uplightFalloff.mul(hLocal).negate()));
              // Ambient by the skin's FACING: the sky's light on what faces up
              // (less in a trough between florets), the lit ground's on what
              // faces down — the flat base. Scalar ramp uniforms stay the
              // WeatherModule night-ramp surface; the tints are the caller's.
              const upShare = clamp(sh.up.mul(0.5).add(0.5), float(0), float(1));
              const ambient = mix(
                vec3(S.ambientBottomColor as never).mul(S.ambientBottom),
                vec3(S.ambientTopColor as never).mul(S.ambientTop).mul(mix(float(CLOUD_CREVICE_AO), float(1), sh.floret)),
                upShare,
              ).add(uplight);

              // THE LIGHTNING HOOK: an emission lobe around an absolute flash point.
              const flash = vec3(1.0, 0.97, 1.05).mul(S.uFlashIntensity)
                .mul(exp(distance(p.sub(uWorldOffset), vec3(S.uFlashPos as never)).div(S.uFlashRadius).negate()));
              const scatter = sunContrib
                .add(ambient).mul(vec3(S.albedo as never)).add(flash);
              scat.addAssign(scatter.mul(trans).mul(float(1).sub(stepT)));
              trans.mulAssign(stepT);
              tCur.addAssign(stepCur);
              stepCur.mulAssign(STEP_GROWTH);
            }).Else(() => {
              // EMPTY: jump toward the CARVED skin (the floret carve retreats the surface up to 0.55 of a turret
              // radius inside the union the first trace found) and keep the step fine — the step only grows
              // INSIDE the cloud, so the samples that meet a trough's surface are as close together as the ones
              // that meet a crest (walking the carve at the growing step left the hollows 20–30 m apart: grain).
              // 0.7: the carve is noise, not a true distance.
              tEmpty.assign(tCur);
              tCur.addAssign(max(stepCur, sh.skip.div(shapePerMetre).mul(0.7)));
            });
          });
        });

        // THE OPAQUE CUT: what the march integrated IS the cloud (see CLOUD_OPAQUE_CUT)
        alpha.assign(min(float(1).sub(trans).mul(1 / (1 - CLOUD_OPAQUE_CUT)), float(1)));
        scat.mulAssign(1 / (1 - CLOUD_OPAQUE_CUT));
      });
    });

    return { scat, alpha, disc, b, a2 };
  };

  return { centerR, r3, hgt, lobeUnion, sampleShape, sunCone, march };
}
